// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

import { getApiBaseUrl } from '@/services/apiBaseUrl';
import type { RecentRoundGameSetup } from '@/features/round-init/types';

type RecentRoundGameResponse = {
  gameDefinitionId?: string;
  GameDefinitionId?: string;
  weightPercent?: number;
  WeightPercent?: number;
  ruleSetId?: string | null;
  RuleSetId?: string | null;
};

type RecentRoundGameSetupResponse = {
  roundId?: string;
  RoundId?: string;
  name?: string;
  Name?: string;
  courseName?: string;
  CourseName?: string;
  holesCount?: number;
  HolesCount?: number;
  playDate?: string | null;
  PlayDate?: string | null;
  createdAt?: string;
  CreatedAt?: string;
  games?: RecentRoundGameResponse[];
  Games?: RecentRoundGameResponse[];
};

export const mapRecentRoundGameSetup = (row: RecentRoundGameSetupResponse): RecentRoundGameSetup | null => {
  const roundId = (row.roundId ?? row.RoundId ?? '').trim();
  const courseName = (row.courseName ?? row.CourseName ?? '').trim();
  const games = (row.games ?? row.Games ?? [])
    .map((game) => ({
      gameDefinitionId: (game.gameDefinitionId ?? game.GameDefinitionId ?? '').trim(),
      weightPercent: game.weightPercent ?? game.WeightPercent ?? 100,
      ruleSetId: game.ruleSetId ?? game.RuleSetId ?? null,
    }))
    .filter((game) => game.gameDefinitionId.length > 0);

  // A round with nothing to copy is not worth offering.
  if (!roundId || games.length === 0) {
    return null;
  }

  return {
    roundId,
    name: (row.name ?? row.Name ?? '').trim() || courseName,
    courseName,
    holesCount: row.holesCount ?? row.HolesCount ?? 18,
    playDate: row.playDate ?? row.PlayDate ?? null,
    createdAt: (row.createdAt ?? row.CreatedAt ?? '').trim(),
    games,
  };
};

/** Short, localized date for a previous round, e.g. "28 Apr". Empty when unknown. */
export function formatRoundSetupDate(setup: RecentRoundGameSetup): string {
  const source = setup.playDate || setup.createdAt;
  if (!source) {
    return '';
  }
  const parsed = new Date(source);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/**
 * Loads the game selections of a player's most recent rounds (newest first), used to offer
 * "copy games from a previous round" on the game-selection screen. Returns an empty list
 * until a valid playerId and token are available.
 */
export function useRecentRoundGameSetups(
  playerId: string | null | undefined,
  token: string | null | undefined,
  limit = 5
): RecentRoundGameSetup[] {
  const [setups, setSetups] = useState<RecentRoundGameSetup[]>([]);

  useEffect(() => {
    if (!playerId || !token) {
      setSetups([]);
      return;
    }

    let cancelled = false;
    const apiBaseUrl = getApiBaseUrl();

    const loadSetups = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/rounds/players/${playerId}/recent-game-setups?limit=${limit}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok || cancelled) {
          return;
        }

        const payload = (await response.json()) as RecentRoundGameSetupResponse[];
        if (cancelled || !Array.isArray(payload)) {
          return;
        }

        setSetups(
          payload
            .map(mapRecentRoundGameSetup)
            .filter((setup): setup is RecentRoundGameSetup => setup !== null)
        );
      } catch {
        // Copying from a previous round is a shortcut; ignore failures silently.
      }
    };

    void loadSetups();
    return () => {
      cancelled = true;
    };
  }, [playerId, token, limit]);

  return setups;
}
